/**
 * SignaturePad — Draw or type an electronic signature.
 *
 * Props:
 *   onChange({ method, drawnImage, typedName }) — called whenever the sig changes
 *   legalName  — pre-fill the typed-name field
 *   disabled   — lock the pad after submission
 *   label      — override the section heading
 */
import { useRef, useEffect, useCallback, useState } from "react";
import SignaturePadLib from "signature_pad";
import * as Icons from "lucide-react";

export function SignaturePad({ onChange, legalName = "", disabled = false, label = "Your Signature" }) {
  const canvasRef = useRef(null);
  const padRef = useRef(null);
  const containerRef = useRef(null);
  const [method, setMethod] = useState("draw");
  const [typedName, setTypedName] = useState(legalName);
  const [hasDrawn, setHasDrawn] = useState(false);

  // Resize canvas to fill container at the correct pixel ratio
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const w = canvas.offsetWidth;
    const h = canvas.offsetHeight;
    if (canvas.width !== w * ratio || canvas.height !== h * ratio) {
      // Preserve existing strokes across resize
      const data = padRef.current?.toData();
      canvas.width = w * ratio;
      canvas.height = h * ratio;
      canvas.getContext("2d").scale(ratio, ratio);
      padRef.current?.clear();
      if (data?.length) padRef.current?.fromData(data);
    }
  }, []);

  // Initialise / destroy SignaturePad when method switches to draw
  useEffect(() => {
    if (method !== "draw" || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const pad = new SignaturePadLib(canvas, {
      backgroundColor: "rgba(255,255,255,0)",
      penColor: "#171B2E",
      minWidth: 1.5,
      maxWidth: 3,
    });
    padRef.current = pad;
    resizeCanvas();

    const onStroke = () => {
      const empty = pad.isEmpty();
      setHasDrawn(!empty);
      onChange({
        method: "drawn",
        drawnImage: empty ? null : pad.toDataURL("image/png"),
        typedName: "",
      });
    };

    pad.addEventListener("afterUpdateStroke", onStroke);
    window.addEventListener("resize", resizeCanvas);

    return () => {
      pad.removeEventListener("afterUpdateStroke", onStroke);
      window.removeEventListener("resize", resizeCanvas);
      pad.off();
      padRef.current = null;
    };
  }, [method, onChange, resizeCanvas]);

  const clearDraw = () => {
    padRef.current?.clear();
    setHasDrawn(false);
    onChange({ method: "drawn", drawnImage: null, typedName: "" });
  };

  const switchMethod = (m) => {
    if (disabled) return;
    setMethod(m);
    if (m === "draw") {
      onChange({ method: "drawn", drawnImage: null, typedName: "" });
    } else {
      onChange({ method: "typed", drawnImage: null, typedName });
    }
  };

  return (
    <div className="space-y-3" data-testid="signature-pad-root">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
        {/* Method toggle */}
        <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs font-medium" data-testid="signature-method-toggle">
          <button
            type="button"
            onClick={() => switchMethod("draw")}
            disabled={disabled}
            data-testid="signature-method-draw"
            className={`flex items-center gap-1.5 px-3 py-1.5 transition-colors ${
              method === "draw"
                ? "bg-[#171B2E] text-white"
                : "bg-white text-slate-500 hover:bg-slate-50"
            }`}
          >
            <Icons.PenTool className="h-3 w-3" />
            Draw
          </button>
          <button
            type="button"
            onClick={() => switchMethod("type")}
            disabled={disabled}
            data-testid="signature-method-type"
            className={`flex items-center gap-1.5 px-3 py-1.5 transition-colors border-l border-slate-200 ${
              method === "type"
                ? "bg-[#171B2E] text-white"
                : "bg-white text-slate-500 hover:bg-slate-50"
            }`}
          >
            <Icons.Type className="h-3 w-3" />
            Type
          </button>
        </div>
      </div>

      {method === "draw" ? (
        <div ref={containerRef} className="relative">
          <canvas
            ref={canvasRef}
            data-testid="signature-canvas"
            style={{
              touchAction: "none",  // Prevent accidental page scroll while drawing
              cursor: disabled ? "default" : "crosshair",
              display: "block",
              width: "100%",
              height: "120px",
            }}
            className={`w-full rounded-xl border-2 bg-white ${
              disabled
                ? "border-slate-200 opacity-70"
                : hasDrawn
                ? "border-[#171B2E]/30"
                : "border-dashed border-slate-300"
            }`}
          />
          {!hasDrawn && !disabled && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-slate-300 text-sm select-none">
              <Icons.PenTool className="h-4 w-4 mr-1.5 opacity-50" />
              Draw your signature here
            </div>
          )}
          {hasDrawn && !disabled && (
            <button
              type="button"
              onClick={clearDraw}
              data-testid="signature-clear-btn"
              className="mt-1.5 flex items-center gap-1 text-xs text-slate-400 hover:text-rose-500 transition-colors"
            >
              <Icons.RotateCcw className="h-3 w-3" />
              Clear &amp; redraw
            </button>
          )}
          {hasDrawn && disabled && (
            <div className="mt-1 flex items-center gap-1 text-xs text-emerald-600">
              <Icons.CheckCircle2 className="h-3 w-3" />
              Signature captured
            </div>
          )}
        </div>
      ) : (
        <div>
          <input
            type="text"
            value={typedName}
            disabled={disabled}
            data-testid="signature-type-input"
            onChange={(e) => {
              setTypedName(e.target.value);
              onChange({ method: "typed", drawnImage: null, typedName: e.target.value });
            }}
            placeholder="Type your full legal name"
            className={`w-full border rounded-xl px-3 py-2.5 text-lg font-serif italic transition-colors ${
              disabled
                ? "bg-slate-50 border-slate-200 text-slate-500 cursor-default"
                : "bg-white border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40 focus:border-[#F5A623]"
            }`}
          />
          <p className="mt-1 text-xs text-slate-400">
            Typing your legal name constitutes your electronic signature.
          </p>
        </div>
      )}
    </div>
  );
}
